/** @class EditableItemsPicker
 *
 * @desc
 * Wraps `ItemPermissionsSelectForm` for the account-manager-controlled "editable items"
 * preset on a firm's write-access settings.
 *
 * Treats `null` as the "no preset" state and defaults the visible selection to every
 * metadata item, so the AM is opting items out rather than starting from an empty list.
 *
 * +onChange+ is fired with `null` whenever the resulting selection covers every known
 * metadata item (the "no preset" representation), and with an explicit array otherwise.
 * This keeps the parent's stored state mirrored to what the backend will persist, so
 * a re-check of every item correctly drops back to `NULL` rather than locking the
 * preset to the current item universe.
 */
import {connect} from 'react-redux'
import PropTypes from 'prop-types'

import ItemPermissionsSelectForm from '../../../../../shared_components/item_permissions/ItemPermissionsSelectForm'

import {selectAllItemIds} from '../../../../../lib/plan_data/itemSelectors'

export const EditableItemsPicker = ({value, onChange, allItemIds}) => {
  const checked = value == null ? allItemIds : value

  const handleCheck = (nextCheckedIds) => {
    if (!allItemIds.length) {
      onChange(nextCheckedIds)
      return
    }
    const nextSelected = new Set(nextCheckedIds)
    const coversAll = nextSelected.size === allItemIds.length &&
      allItemIds.every(id => nextSelected.has(id))
    onChange(coversAll ? null : nextCheckedIds)
  }

  return (
    <ItemPermissionsSelectForm
      checkedItemIds={checked}
      onCheck={handleCheck}
    />
  )
}

EditableItemsPicker.propTypes = {
  // `null` is a supported state meaning "no preset"; the picker then renders all items selected.
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.oneOf([null])
  ]),
  onChange: PropTypes.func.isRequired,
  allItemIds: PropTypes.arrayOf(PropTypes.string).isRequired
}

const mapStateToProps = state => ({
  allItemIds: selectAllItemIds(state)
})

export default connect(mapStateToProps)(EditableItemsPicker)
